import React, {useState, useContext, useEffect, useMemo, useCallback, useRef} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faCheckDouble, faEdit} from "@fortawesome/free-solid-svg-icons";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {uploadSingleFile, megabytesToBytes, uploadErrorMessage} from "../../utils/fileUpload.js";
import {useToast} from "../../contexts/toast.js";

// Components
import InputError from "./InputError.js";
import Help from "../Help.js";
import FieldLinkHelp from "./FieldLinkHelp.js";

// Style
import {voice} from "../../style/components/typography.js";
import {flex} from "../../style/components/mixins.js";
import {colors, pad} from "../../style/components/variables.js";
import {cross} from "../../style/components/shapes.js";
import {
  FormFieldWrapper,
  Label,
  Text,
  Loader,
  Button,
  Pill,
  Abbr,
  Inline,
  Small,
  Input
} from "../../style/components/general.js";
import {exists} from "../../utils/helpers.js";

const InputFile = ({
  name,
  prompt,
  label,
  condition,
  tag,
  shared = false,
  linkedAcrossStates = false,
  required = true,
  disabled = false
}) => {
  const isMounted = useMountedState();

  const {currentUser} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);
  const {addToast} = useToast();

  const [filesToUpload, setFilesToUpload] = useState(null);
  const [files, setFiles] = useState(null);
  const [savedFiles, setSavedFiles] = useState(null);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [updatedName, setUpdatedName] = useState(null);
  const [uploading, setUploading] = useState(false);

  const outside = useRef();
  const confirm = useRef();

  const maxBytes = useMemo(() => {
    let {maxFileSize: current} = settings;
    if (!current) current = 50;
    else {
      current = parseInt(current, 10);
      if (!current || Number.isNaN(current)) current = 50;
      else if (current > 100) current = 100;
    }
    return megabytesToBytes(current);
  }, [settings]);

  const images = useMemo(
    () => savedFiles?.filter(savedFile => savedFile.extension?.name.includes("image")),
    [savedFiles]
  );

  const nonImages = useMemo(
    () => savedFiles?.filter(savedFile => !savedFile.extension?.name.includes("image")),
    [savedFiles]
  );

  const {api, loading} = useApi("files");
  const {api: updateApi} = useApi("files", {suppress: {success: true, error: false}});

  const {
    setValue,
    trigger,
    watch,
    formState: {errors, submitCount}
  } = useFormContext();

  const uploaded = watch(name);

  useEffect(() => {
    const handleOutsideClick = e => {
      if (
        exists(fileToEdit) &&
        outside.current &&
        !outside.current.contains(e.target) &&
        !confirm.current?.contains(e.target)
      ) {
        setFileToEdit(null);
        setUpdatedName(null);
      }
    };

    if (outside.current) document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [fileToEdit]);

  const removeFile = idx => {
    setFiles(old => (old ? old.filter((_f, i) => i !== idx) : null));
    setSavedFiles(old => (old ? old.filter((_f, i) => i !== idx) : null));
    if (uploaded) {
      const fileIds = uploaded.split(",");
      setValue(name, fileIds.filter((_f, i) => i !== idx).join(","), {shouldDirty: true});
    }
  };

  useEffect(() => {
    if (isMounted() && !files && uploaded && uploaded !== "_CONDITION_UNSATISFIED") {
      api.callGet(null, {ids: uploaded.split(","), getSerialized: true}).then(({status, data}) => {
        if (status === 200 && data) {
          const {files: fileData, links} = data;

          if (fileData) {
            setSavedFiles(
              Object.keys(fileData)
                .filter(id => {
                  const fileObj = fileData[id];
                  const {fileName, extension} = fileObj || {};
                  const link = links[id];
                  return link && fileName && extension?.name;
                })
                .map(id => {
                  const fileObj = fileData[id];
                  const link = links[id];
                  return {...fileObj, link};
                })
            );
            setFiles(Object.keys(fileData).map(() => null));
          }
        }
      });
    }
  }, [isMounted, uploaded, files, api]);

  useEffect(() => {
    if (!(isMounted() && currentUser?.publicId && filesToUpload)) return undefined;

    let cancelled = false;
    setUploading(true);

    (async () => {
      try {
        const results = await Promise.all(
          filesToUpload.map(f =>
            uploadSingleFile(f, {
              userId: currentUser.publicId,
              maxBytes
            })
          )
        );
        if (cancelled) return;

        if (results.length > 0) {
          setSavedFiles(prev => (prev ? [...prev, ...results] : [...results]));

          let fileIdStr = uploaded;
          results.forEach(fileObj => {
            const {id} = fileObj || {};
            if (id) {
              if (fileIdStr) fileIdStr = `${fileIdStr},${id}`;
              else fileIdStr = `${id}`;
            }
          });

          setValue(name, fileIdStr, {shouldDirty: true});

          if (submitCount > 0) trigger(name);
        }
      } catch (e) {
        addToast(uploadErrorMessage(e), "error");
      } finally {
        if (!cancelled) {
          setUploading(false);
          setFilesToUpload(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isMounted,
    currentUser,
    filesToUpload,
    name,
    setValue,
    uploaded,
    trigger,
    submitCount,
    maxBytes,
    addToast
  ]);

  const updateFileName = useCallback(
    (targetId, n) => {
      if (targetId)
        updateApi.callPut(targetId, {name: n}).then(({status, data}) => {
          if (status === 200 && data?.file) {
            setSavedFiles(prev =>
              prev.map(f => (f.id === targetId ? {...f, label: data.file.label} : f))
            );

            setFileToEdit(null);
            setUpdatedName(null);
          }
        });
    },
    [updateApi]
  );

  useEffect(() => {
    if (exists(fileToEdit)) {
      document.getElementById("editName")?.focus?.();
    }
  }, [fileToEdit]);

  const showLoader = loading || uploading;

  return (
    <FormFieldWrapper>
      {label && (
        <LabelWrapper htmlFor={name} bold inline>
          {label.toUpperCase()}
          {required && <span>*</span>}
          {tag && (
            <Pill quiet>
              <Abbr title={tag}>{tag}</Abbr>
            </Pill>
          )}
          <FieldLinkHelp shared={shared} linkedAcrossStates={linkedAcrossStates} />
          {condition && <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>{condition}</Help>}
        </LabelWrapper>
      )}
      <FileWrapper id={name}>
        {prompt && <Prompt quiet>{prompt}</Prompt>}
        <Upload type="button" disabled={disabled} data-testid={`${name}_visibleUpload`}>
          <FileInputLabel
            htmlFor={disabled ? undefined : `${name}_hiddenUpload`}
            disabled={disabled}>
            <span>Upload</span>
            <HiddenInput
              id={`${name}_hiddenUpload`}
              type="file"
              data-testid="file.upload"
              multiple
              disabled={disabled}
              onChange={({target}) => {
                if (disabled) return;
                const tempFiles = target?.files ? Object.values(target.files) : null;

                if (tempFiles) {
                  setFiles(prev => {
                    const temp = prev
                      ? [
                          ...prev,
                          ...tempFiles.map(file => ({...file, link: URL.createObjectURL(file)}))
                        ]
                      : [...tempFiles.map(file => ({...file, link: URL.createObjectURL(file)}))];

                    return temp;
                  });
                  setFilesToUpload(tempFiles);
                  target.value = "";
                }
              }}
            />
          </FileInputLabel>
        </Upload>
      </FileWrapper>

      {uploaded && !showLoader && images?.length > 0 && (
        <FileRow>
          {savedFiles.map((savedFile, i) => {
            const file = files ? files[i] : null;
            if (!savedFile || !savedFile.extension?.name.includes("image")) return null;

            return (
              <OuterWrapper key={`${name}${savedFile.fileName}`}>
                <Preview
                  background={file ? file.link : savedFile?.link}
                  data-testid="preview"
                  isImage>
                  <Delete
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      removeFile(i);
                    }}>
                    <X />
                  </Delete>
                  <Edit
                    isImage
                    disabled={disabled}
                    ref={fileToEdit === i ? confirm : undefined}
                    onClick={() => {
                      if (disabled) return;
                      if (fileToEdit !== i) {
                        setUpdatedName(savedFile.label);
                        setFileToEdit(i);
                      } else {
                        updateFileName(savedFile.id, updatedName);
                      }
                    }}>
                    <FontAwesomeIcon icon={fileToEdit === i ? faCheck : faEdit} />
                  </Edit>
                </Preview>
                {fileToEdit === i ? (
                  <FileNameInput
                    id="editName"
                    type="text"
                    disabled={disabled}
                    onChange={e => setUpdatedName(e.target.value)}
                    value={updatedName}
                    ref={outside}
                  />
                ) : (
                  <FileName data-testid="file_label">
                    {file?.name ? file.name : savedFile?.label}
                  </FileName>
                )}
              </OuterWrapper>
            );
          })}
        </FileRow>
      )}
      {uploaded && !showLoader && nonImages?.length > 0 && (
        <FileRow extraMargin={images?.length > 0 ? 1 : 0}>
          {savedFiles.map((savedFile, i) => {
            const file = files ? files[i] : null;

            if (!savedFile || savedFile.extension?.name.includes("image")) return null;
            return (
              <OuterWrapper key={`${name}${savedFile.fileName}`}>
                <Preview>
                  {fileToEdit === i ? (
                    <FileNameInput
                      id="editName"
                      type="text"
                      disabled={disabled}
                      onChange={e => setUpdatedName(e.target.value)}
                      value={updatedName}
                      ref={outside}
                    />
                  ) : (
                    <FileNameLink
                      href={file ? file.link : savedFile?.link}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="file_label"
                      download={savedFile.label}>
                      {file?.name ? file.name : savedFile?.label}
                    </FileNameLink>
                  )}
                  <Delete
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      removeFile(i);
                    }}
                    noborder>
                    <X />
                  </Delete>
                  <Edit
                    disabled={disabled}
                    ref={fileToEdit === i ? confirm : undefined}
                    onClick={() => {
                      if (disabled) return;
                      if (fileToEdit !== i) {
                        setUpdatedName(savedFile.label);
                        setFileToEdit(i);
                      } else {
                        updateFileName(savedFile.id, updatedName);
                      }
                    }}>
                    <FontAwesomeIcon icon={fileToEdit === i ? faCheck : faEdit} />
                  </Edit>
                </Preview>
              </OuterWrapper>
            );
          })}
        </FileRow>
      )}
      {showLoader && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      <InputError errors={errors} name={name} />
    </FormFieldWrapper>
  );
};

InputFile.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  prompt: PropTypes.string,
  condition: PropTypes.string,
  tag: PropTypes.string,
  shared: PropTypes.bool,
  linkedAcrossStates: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool
};

// Style
const FileWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: fit-content;
`;

const Prompt = styled(Text)`
  margin-bottom: ${pad / 2}px;
`;

const Upload = styled(Button)`
  margin-top: ${pad / 2}px;
`;

const FileInputLabel = styled.label`
  ${voice.normal};
  position: relative;

  svg {
    pointer-events: none;
    fill: ${({theme}) => theme.tertiary};
  }

  ${({disabled}) =>
    disabled &&
    css`
      &:hover {
        cursor: not-allowed;
      }
    `}
`;

const FileNameInput = styled(Input)`
  width: 115px;
  border: none;
  border-radius: 0;
  height: 20px;
  text-overflow: ellipsis;

  &[type="text"] {
    width: 115px;
  }
`;

const HiddenInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const OuterWrapper = styled.div`
  margin: ${pad}px ${pad}px 0 0;
  background-color: ${colors.grayDark};
  height: max-content;
`;

const FileName = styled(Small)`
  width: 110px;
  display: block;
  color: ${colors.heroBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 5px;
`;

const FileNameLink = styled.a`
  width: 110px;
  display: block;
  color: ${colors.heroBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 5px;
`;

const Preview = styled.div`
  ${flex("row", "nowrap", "center", "center")}
  text-align: center;
  border: 2px solid ${({noborder, theme}) => (noborder ? "transparent" : theme.primary)};
  position: relative;
  width: 120px;
  padding-bottom: 0px;
  background-image: url(${({background}) => background});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${colors.grayLight};
  ${voice.small}

  ${({isImage}) =>
    isImage
      ? css`
          height: 100px;
        `
      : css`
          height: 30px;
        `}
`;

const LoaderContainer = styled.div`
  width: 200px;
  height: 60px;
  position: relative;
`;

const Delete = styled.div`
  background-color: ${colors.grayDarker};
  border-radius: 50%;
  height: 20px;
  width: 20px;
  position: absolute;
  cursor: pointer;

  right: -13px;
  top: -10px;

  ${({disabled}) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.4;
    `}
`;

const Edit = styled.div`
  background-color: ${colors.grayDarker};
  border-radius: 50%;
  height: 20px;
  width: 20px;
  position: absolute;
  cursor: pointer;

  right: -13px;
  bottom: ${({isImage}) => (isImage ? -28 : -10)}px;

  svg {
    ${voice.quiet};
    margin-left: 1px;
    margin-bottom: -1px;
    fill: ${colors.heroWhite};
  }

  ${({disabled}) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.4;
    `}
`;

const X = styled.div`
  ${cross(undefined, colors.heroWhite)}
  :before {
    height: 60%;
    top: 20%;
    width: 1.5px;
  }

  :after {
    height: 60%;
    top: 20%;
    width: 1.5px;
  }
`;

const FileRow = styled(Inline)`
  flex-wrap: wrap;
  row-gap: 20px;

  ${({extraMargin}) =>
    extraMargin &&
    css`
      margin-top: ${pad * 2}px;
    `}
`;

const LabelWrapper = styled(Label)`
  gap: ${pad / 2}px;
  margin-bottom: 2px;
`;

export default InputFile;
